/**
 * Date picker compact : champ cliquable → bottom sheet calendrier.
 * Inspiré du design de calendar-mobile-page.tsx
 */
import { useState, useMemo, useEffect, useCallback } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatDisplay(d: Date, locale: string): string {
  return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
}

function buildMonthGrid(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1)
  let startDow = firstDay.getDay() - 1
  if (startDow < 0) startDow = 6

  const cells: Date[] = []
  const startDate = new Date(year, month, 1 - startDow)
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate)
    d.setDate(startDate.getDate() + i)
    cells.push(d)
  }

  const lastRow = cells.slice(35)
  if (lastRow.every((d) => d.getMonth() !== month)) {
    return cells.slice(0, 35)
  }
  return cells
}

interface InlineDatePickerProps {
  /** Date sélectionnée au format YYYY-MM-DD (ou chaîne vide) */
  value: string
  /** Callback avec la date sélectionnée au format YYYY-MM-DD */
  onChange: (dateStr: string) => void
  /** Date min au format YYYY-MM-DD — les jours avant seront désactivés */
  min?: string
  /** Couleur d'accent hex (défaut : primary via Tailwind) */
  accent?: string
  /** Placeholder quand aucune date n'est sélectionnée */
  placeholder?: string
  /** Callback optionnel pour filtrer les dates sélectionnables */
  isDateEnabled?: (date: Date) => boolean
}

export function InlineDatePicker({ value, onChange, min, accent, placeholder, isDateEnabled }: InlineDatePickerProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
  const monthNames = t('mobile.months', { returnObjects: true }) as string[]
  const weekdayHeaders = t('mobile.weekdayHeaders', { returnObjects: true }) as string[]
  const displayPlaceholder = placeholder ?? t('mobile.datePicker.placeholder')
  const [open, setOpen] = useState(false)

  const selectedDate = value ? new Date(value + 'T00:00:00') : null
  const minDate = min ? new Date(min + 'T00:00:00') : null

  const [viewMonth, setViewMonth] = useState(() => {
    if (selectedDate) return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  })

  // Sync viewMonth when value changes externally
  useEffect(() => {
    if (selectedDate) {
      setViewMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1))
    }
  }, [value])

  const cells = useMemo(
    () => buildMonthGrid(viewMonth.getFullYear(), viewMonth.getMonth()),
    [viewMonth],
  )

  const today = useMemo(() => {
    const t = new Date()
    t.setHours(0, 0, 0, 0)
    return t
  }, [])

  const goMonth = (delta: number) => {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
  }

  const handleSelect = useCallback((d: Date) => {
    if (minDate && d < minDate) return
    if (isDateEnabled && !isDateEnabled(d)) return
    onChange(toDateStr(d))
    setOpen(false)
  }, [minDate, isDateEnabled, onChange])

  const accentRing = accent ? `${accent}40` : undefined

  return (
    <>
      {/* Trigger — compact field */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex h-11 w-full items-center gap-3 rounded-xl border bg-white px-4 text-left text-sm transition-all active:scale-[0.99]',
          selectedDate
            ? 'border-neutral-200 text-neutral-900'
            : 'border-dashed border-neutral-300 text-neutral-400',
        )}
      >
        <CalendarDays
          className="h-4 w-4 shrink-0"
          style={{ color: selectedDate && accent ? accent : undefined }}
        />
        <span className={cn('flex-1 truncate', selectedDate ? 'font-medium' : '')}>
          {selectedDate ? formatDisplay(selectedDate, locale) : displayPlaceholder}
        </span>
        {selectedDate && accent && (
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: accent }}
          />
        )}
      </button>

      {/* Bottom sheet overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center"
          onClick={() => setOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />

          {/* Sheet */}
          <div
            className="relative w-full max-w-md animate-[slideUp_0.25s_ease-out] rounded-t-3xl bg-white pb-[max(env(safe-area-inset-bottom,16px),16px)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pb-1 pt-3">
              <div className="h-1 w-10 rounded-full bg-neutral-200" />
            </div>

            {/* Month navigation */}
            <div className="flex items-center justify-between px-5 py-2">
              <button
                type="button"
                onClick={() => goMonth(-1)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors active:bg-neutral-100"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-sm font-bold text-neutral-800">
                {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
              </span>
              <button
                type="button"
                onClick={() => goMonth(1)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors active:bg-neutral-100"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 px-4">
              {weekdayHeaders.map((l, i) => (
                <div key={i} className="py-1 text-center text-[10px] font-bold uppercase tracking-wider text-neutral-300">
                  {l}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-px px-4 pb-4">
              {cells.map((date, i) => {
                const inMonth = date.getMonth() === viewMonth.getMonth()
                const isDayToday = isSameDay(date, today)
                const isSelected = selectedDate ? isSameDay(date, selectedDate) : false
                const isDisabled = !!(minDate && date < minDate) || !!(isDateEnabled && !isDateEnabled(date))

                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => !isDisabled && handleSelect(date)}
                    disabled={isDisabled}
                    className={cn(
                      'relative flex h-11 items-center justify-center rounded-xl transition-all',
                      !inMonth && 'opacity-20',
                      isDisabled && inMonth && 'opacity-30 cursor-not-allowed',
                      !isSelected && !isDisabled && inMonth && 'active:bg-neutral-50',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-full text-sm transition-all',
                        isSelected && !accent && 'bg-primary font-bold text-white shadow-sm shadow-primary/30',
                        isDayToday && !isSelected && 'bg-primary/10 font-bold text-primary',
                        !isDayToday && !isSelected && (inMonth ? 'font-medium text-neutral-700' : 'text-neutral-400'),
                      )}
                      style={
                        isSelected && accent
                          ? { backgroundColor: accent, color: '#fff', fontWeight: 700, boxShadow: `0 2px 8px ${accentRing}` }
                          : undefined
                      }
                    >
                      {date.getDate()}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Slide-up animation */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  )
}
